import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import {
	TransMipManage,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	Box,
	Button,
	Flex,
	Group,
	Select,
	Stack,
	Text,
	TextInput,
	useMantineTheme,
} from '@mantine/core';
import { useState } from 'react';
import type { z } from 'zod';
import type { MasterDocumentCounterpartySchema } from './types';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

type CounterpartyRoleItem = z.infer<typeof MasterDocumentCounterpartySchema>;

const SIGNATURES_OPTIONS = [
	{ value: '1', label: '1' },
	{ value: '2', label: '2' },
];

interface Props {
	value: CounterpartyRoleItem[];
	onChange: (value: CounterpartyRoleItem[]) => void;
	disabled?: boolean;
	label?: string;
	showWitnessCheckbox?: boolean;
}

export const CounterpartyNamesInput = ({
	value = [],
	onChange,
	disabled = false,
	label,
	showWitnessCheckbox = false,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const theme = useMantineTheme();
	const [inputName, setInputName] = useState('');
	const [inputSignatures, setInputSignatures] = useState<string>('1');

	const handleAdd = () => {
		const name = inputName.trim();
		const existingNames = value.map((cp) => cp.name);
		const num = parseInt(inputSignatures, 10) as 1 | 2;
		if (name && !existingNames.includes(name) && (num === 1 || num === 2)) {
			onChange([...value, { name, numberOfSignaturesRequired: num, requiresWitness: false }]);
			setInputName('');
			setInputSignatures('1');
		}
	};

	const handleRemove = (index: number) => {
		onChange(value.filter((_, i) => i !== index));
	};

	const handleSignaturesChange = (index: number, valueStr: string | null) => {
		const n = valueStr ? (parseInt(valueStr, 10) as 1 | 2) : 1;
		if (n !== 1 && n !== 2) {
			return;
		}
		onChange(
			value.map((cp, i) => (i === index ? { ...cp, numberOfSignaturesRequired: n } : cp))
		);
	};

	const handleWitnessChange = (index: number, checked: boolean) => {
		onChange(value.map((cp, i) => (i === index ? { ...cp, requiresWitness: checked } : cp)));
	};

	return (
		<Stack gap="xs">
			<Text size="sm" fw={600}>
				{label ?? tMipManage('master_documents.text.counterparty_roles')}
			</Text>
			<Flex w="100%" gap="xs">
				<TextInput
					placeholder={tMipManage('master_documents.text.counterparty_placeholder')}
					value={inputName}
					onChange={(e) => setInputName(e.currentTarget.value)}
					onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
					disabled={disabled}
					size="md"
					w="80%"
					style={{ flex: 1, minWidth: 0 }}
				/>
				<Select
					value={inputSignatures}
					onChange={(val) => setInputSignatures(val ?? '1')}
					data={SIGNATURES_OPTIONS}
					disabled={disabled}
					size="md"
					w="20%"
					title={tMipManage('master_documents.text.counterparty_signatures')}
				/>
				<Button
					variant="secondary"
					size="md"
					onClick={handleAdd}
					disabled={disabled || !inputName.trim()}
					miw="48px"
				>
					<Icon variant="add" size="lg" />
				</Button>
			</Flex>
			{value.length > 0 && (
				<Stack gap="xs">
					{value.map((cp, index) => (
						<Stack
							key={`${cp.name}-${index}`}
							data-testid={`counterparty-row-${index}`}
							gap={0}
							sx={(theme) => ({
								borderLeft: `3px solid ${theme.colors.blue[6]}`,
								borderRadius: theme.radius.sm,
								overflow: 'hidden',
							})}
						>
							<Group justify="space-between" align="center" p="xs" wrap="nowrap">
								<Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
									<Icon variant="building-office-company-entity-line" size="lg" />
									<Text size="sm" fw={600} truncate>
										{cp.name}
									</Text>
								</Group>
								<Group gap="xs" wrap="nowrap">
									<Text size="xs" c="dimmed">
										{tMipManage('master_documents.text.signatures_count_label')}
									</Text>
									<Select
										value={String(cp.numberOfSignaturesRequired)}
										onChange={(val) => handleSignaturesChange(index, val)}
										data={SIGNATURES_OPTIONS}
										disabled={disabled}
										size="xs"
										styles={{ root: { width: 60 } }}
										title={tMipManage(
											'master_documents.text.counterparty_signatures'
										)}
									/>
									<Icon
										variant="trash-can"
										size="lg"
										onClick={disabled ? undefined : () => handleRemove(index)}
										style={{
											cursor: disabled ? 'not-allowed' : 'pointer',
											opacity: disabled ? 0.4 : 1,
										}}
									/>
								</Group>
							</Group>

							{showWitnessCheckbox && (
								<Box
									px="xs"
									py="xs"
									sx={(theme) => ({
										borderTop: `1px solid ${theme.colors.gray[2]}`,
										backgroundColor: theme.colors.gray[0],
									})}
								>
									<Group justify="space-between" align="center" wrap="nowrap">
										<Group gap="xs" wrap="nowrap">
											<Icon variant="user-outline" size="lg" />
											<Text size="sm" fw={600}>
												{tMipManage(
													'master_documents.text.counterparty_witness_required'
												)}
											</Text>
										</Group>
										<AdvancedSwitch
											selectedValue={cp.requiresWitness ?? false}
											onChange={(e) => handleWitnessChange(index, !!e)}
											disabled={disabled}
										/>
									</Group>

									{cp.requiresWitness && (
										<Group
											align="flex-start"
											gap={8}
											wrap="nowrap"
											mt="xs"
											p="xs"
											sx={(theme) => ({
												border: `1px solid ${theme.colors.gray[2]}`,
												borderRadius: theme.radius.sm,
												backgroundColor: theme.white,
											})}
										>
											<Icon
												variant="information"
												size="md"
												color={theme.colors.blue[6]}
												style={{ marginTop: 2, flexShrink: 0 }}
											/>
											<Text size="xs">
												<TransMipManage
													i18nKey="master_documents.text.counterparty_witness_applies_to_signatories"
													defaults="Applies to <strong>every signatory</strong> for {{name}}. Each of the {{count}} signatures is signed in front of its own witness."
													values={{
														name: cp.name,
														count: cp.numberOfSignaturesRequired,
													}}
													components={{ strong: <strong /> }}
												/>
											</Text>
										</Group>
									)}
								</Box>
							)}
						</Stack>
					))}
				</Stack>
			)}
		</Stack>
	);
};
